import { ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell } from 'recharts';
import { IBCS } from './HatchPattern';
import IbcsTooltip, { formatMoney } from './IbcsTooltip';
import type { ForecastWeek } from '../../../types/cashCockpit';

/**
 * IBCS-conformant cash waterfall for a multi-week forecast.
 * Shows Opening → Inflows → Outflows → Closing per week as stacked bars.
 *
 * Visualization choices:
 * - Opening/Closing = subtotal (medium grey, IBCS.actualLight)
 * - Inflows = positive (green, IBCS.positive)
 * - Outflows = negative (red, IBCS.negative)
 * - Forecast weeks have hatched overlay to signal prediction
 */
interface WaterfallChartProps {
    data: ForecastWeek[];
    height?: number;
    /** Currency of the numeric fields in `data`. Defaults to EUR. */
    displayCurrency?: string;
}

export default function WaterfallChart({ data, height = 320, displayCurrency = 'EUR' }: WaterfallChartProps) {
    // Transform each week into a single row usable by recharts stacked bars
    const chartData = data.map(w => {
        const inflow = w.arInflowEUR + w.plannedInflowEUR;
        const outflow = w.apOutflowEUR + w.plannedOutflowEUR; // already negative
        return {
            weekLabel: `W${w.weekNumber}`,
            weekStart: w.weekStart,
            opening: w.openingCashEUR,
            closing: w.closingCashEUR,
            inflow,
            outflow: Math.abs(outflow),
            netChange: w.netChangeEUR,
            arInflow: w.arInflowEUR,
            apOutflow: w.apOutflowEUR,
            plannedInflow: w.plannedInflowEUR,
            plannedOutflow: w.plannedOutflowEUR,
        };
    });

    const fmt = (n: number) => formatMoney(n, displayCurrency);
    const tooltipFn = (props: { active?: boolean; payload?: Array<{ payload: (typeof chartData)[number] }> }) => {
        if (!props.active || !props.payload?.[0]) return null;
        const w = props.payload[0].payload;
        return (
            <IbcsTooltip
                title={`Week ${w.weekLabel} · ${w.weekStart}`}
                rows={[
                    { label: 'Opening', value: fmt(w.opening) },
                    { label: 'AR Inflow', value: fmt(w.arInflow), valueClass: 'text-[#2C7BB6]' },
                    { label: 'Planned Inflow', value: fmt(w.plannedInflow), valueClass: 'text-[#2C7BB6]' },
                    { label: 'AP Outflow', value: fmt(w.apOutflow), valueClass: 'text-[#D7191C]' },
                    { label: 'Planned Outflow', value: fmt(w.plannedOutflow), valueClass: 'text-[#D7191C]' },
                    { label: 'Net Change', value: fmt(w.netChange), valueClass: w.netChange >= 0 ? 'text-[#2C7BB6]' : 'text-[#D7191C]' },
                    { label: 'Closing', value: fmt(w.closing), valueClass: 'font-semibold' },
                ]}
            />
        );
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={chartData} margin={{ top: 16, right: 16, left: 16, bottom: 8 }}>
                <CartesianGrid stroke={IBCS.gridLine} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} stroke={IBCS.actualLight} />
                <YAxis tick={{ fontSize: 11 }} stroke={IBCS.actualLight} tickFormatter={v => fmt(v)} width={72} />
                <ReferenceLine y={0} stroke={IBCS.actual} strokeWidth={1} />
                <Tooltip content={tooltipFn as never} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                {/* Closing cash bar (main value) — hatched pattern for forecast character */}
                <Bar dataKey="closing" name="Closing Cash" radius={[2, 2, 0, 0]}>
                    {chartData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.closing >= 0 ? IBCS.forecastPattern : IBCS.negative} stroke={IBCS.actual} strokeWidth={0.5} />
                    ))}
                </Bar>
            </ComposedChart>
        </ResponsiveContainer>
    );
}
